import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const MEMBER_KEY = '__member__';

function DiscordSettings() {
  const [roles, setRoles]         = useState([]);
  const [users, setUsers]         = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  // Form state for new role mapping
  const [newKey, setNewKey]       = useState('');
  const [newRoleId, setNewRoleId] = useState('');
  const [newLabel, setNewLabel]   = useState('');

  // Member role
  const [memberRoleId, setMemberRoleId] = useState('');
  const [memberLabel, setMemberLabel]   = useState('Mitglied');

  const fetchRoles = useCallback(async () => {
    try {
      const r = await api.get('/auth/discord/roles');
      setRoles(r.data);
      const member = r.data.find(c => c.key === MEMBER_KEY);
      if (member) {
        setMemberRoleId(member.role_id);
        setMemberLabel(member.label || 'Mitglied');
      }
    } catch {
      // ignore
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await api.get('/auth/discord/users?limit=200');
      setUsers(r.data.users || []);
      setTotalUsers(r.data.total || 0);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, [fetchRoles, fetchUsers]);

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else          { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 3000);
  };

  const saveMemberRole = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/auth/discord/roles/${MEMBER_KEY}`, {
        role_id: memberRoleId,
        label:   memberLabel || 'Mitglied',
      });
      flash('Mitglieder-Rolle gespeichert.');
      fetchRoles();
    } catch {
      flash('Fehler beim Speichern.', true);
    } finally {
      setLoading(false);
    }
  };

  const addRoleMapping = async (e) => {
    e.preventDefault();
    if (!newKey.trim() || !newRoleId.trim()) return;
    setLoading(true);
    try {
      await api.put(`/auth/discord/roles/${encodeURIComponent(newKey.trim())}`, {
        role_id: newRoleId.trim(),
        label:   newLabel.trim() || newKey.trim(),
      });
      setNewKey(''); setNewRoleId(''); setNewLabel('');
      flash('Rolle gespeichert.');
      fetchRoles();
    } catch {
      flash('Fehler beim Speichern.', true);
    } finally {
      setLoading(false);
    }
  };

  const deleteRole = async (key) => {
    if (!window.confirm(`Rollenzuweisung für "${key}" löschen?`)) return;
    setLoading(true);
    try {
      await api.delete(`/auth/discord/roles/${encodeURIComponent(key)}`);
      flash('Rolle entfernt.');
      fetchRoles();
    } catch {
      flash('Fehler beim Löschen.', true);
    } finally {
      setLoading(false);
    }
  };

  const programRoles = roles.filter(r => r.key !== MEMBER_KEY);

  return (
    <div>
      <div className="page-heading">
        <h2>Discord</h2>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* ── Mitglieder-Rolle ─────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <p className="card-title">Basis-Mitgliederrolle</p>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
          Diese Discord-Rolle bekommt jeder, der sich über OAuth verbindet.
        </p>
        <form onSubmit={saveMemberRole}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Role-ID</label>
              <input
                type="text"
                className="form-input"
                value={memberRoleId}
                onChange={e => setMemberRoleId(e.target.value)}
                placeholder="z. B. 1194391227206996050"
                disabled={loading}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Anzeigename</label>
              <input
                type="text"
                className="form-input"
                value={memberLabel}
                onChange={e => setMemberLabel(e.target.value)}
                placeholder="Mitglied"
                disabled={loading}
              />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Programm-Rollen ──────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <p className="card-title">Programm-Rollen</p>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
          Nutzer erhalten diese Rollen automatisch, wenn sie eine aktive Lizenz für das entsprechende Programm besitzen.
          Der Schlüssel muss exakt dem Programm-Namen in der Datenbank entsprechen.
        </p>

        {programRoles.length > 0 && (
          <div className="table-wrap" style={{ marginBottom: '16px' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Programm-Name (Schlüssel)</th>
                  <th>Discord Role-ID</th>
                  <th>Label</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {programRoles.map(r => (
                  <tr key={r.key}>
                    <td style={{ fontWeight: 600 }}>{r.key}</td>
                    <td><span className="mono">{r.role_id}</span></td>
                    <td style={{ color: 'var(--text-muted)' }}>{r.label || '–'}</td>
                    <td>
                      <button
                        onClick={() => deleteRole(r.key)}
                        className="btn btn-danger btn-sm"
                        disabled={loading}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form onSubmit={addRoleMapping}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Programm-Name</label>
              <input
                type="text"
                className="form-input"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                placeholder="z. B. Familytool"
                disabled={loading}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Role-ID</label>
              <input
                type="text"
                className="form-input"
                value={newRoleId}
                onChange={e => setNewRoleId(e.target.value)}
                placeholder="z. B. 1194391227206996051"
                disabled={loading}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Label (optional)</label>
              <input
                type="text"
                className="form-input"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                placeholder="Anzeigename"
                disabled={loading}
              />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" disabled={loading || !newKey.trim() || !newRoleId.trim()} className="btn btn-primary">
              {loading ? 'Speichern…' : 'Hinzufügen'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Discord Nutzer ───────────────────────────────────────── */}
      <div className="section-header">
        <h3>Verbundene Discord-Nutzer</h3>
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{totalUsers} gesamt</span>
      </div>

      {users.length === 0 ? (
        <div className="empty-state">Noch keine Nutzer verbunden.</div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Server</th>
                <th>Rollen</th>
                <th>Zuletzt gesehen</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ width: '44px' }}>
                    {u.avatar_url
                      ? <img src={u.avatar_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                      : <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#5865F2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14 }}>🎮</div>
                    }
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{u.display_name || u.username}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>@{u.username}</div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{u.email || '–'}</td>
                  <td>
                    {u.guild_joined
                      ? <span style={{ color: '#10b981', fontSize: 13 }}>✓ Beigetreten</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>–</span>
                    }
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {u.assigned_roles && u.assigned_roles.filter(Boolean).length > 0
                      ? u.assigned_roles.filter(Boolean).join(', ')
                      : <span style={{ color: 'var(--text-muted)' }}>–</span>
                    }
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(u.last_seen_at).toLocaleString('de-DE')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DiscordSettings;
